import React from 'react';
import { language } from '../../services/LanguageService';
import { AsperaForm } from '../AsperaForm';
import { SubManager } from '../../classes/SubManager';
import { observableTimeout } from '../../helpers/starterHelpers';
/**
 * React Component
 *
 * The AsperaExpirationPolicy render the expiration policy component
 *
 * @example
 * ```javascript
 * <AsperaExpirationPolicy model={model} pointers={pointers} />
 * ```
 */
export class AsperaExpirationPolicy extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            loading: true,
        };
        this.expirationData = {
            policy: 'none',
            time: '0',
            dateString: undefined,
        };
        this.subManager = new SubManager();
        this.validCheck = () => {
            const { isInternalFormValidCallback, maxDateSeconds, disableDownloadNever } = this.props;
            if (typeof isInternalFormValidCallback === 'function') {
                const isExpirationTimeValid = maxDateSeconds ? !!(this.expirationData.time && (this.expirationData.time !== 'custom' || this.expirationData.dateString)) : true;
                const isExpirationPolicyValid = disableDownloadNever ? !!(this.expirationData.policy && this.expirationData.policy !== 'none') : true;
                isInternalFormValidCallback(isExpirationTimeValid && isExpirationPolicyValid);
            }
        };
        this.validTimeCheck = (value) => {
            const { maxDateSeconds } = this.props;
            return !(typeof maxDateSeconds === 'number' && value > maxDateSeconds);
        };
    }
    setupModel() {
        const { pointers, model } = this.props;
        if (pointers.expirationPolicy) {
            this.expirationData.policy = model[pointers.expirationPolicy];
        }
        if (pointers.expirationDate) {
            this.expirationData.time = model[pointers.expirationDate];
        }
        if (pointers.expirationDate) {
            if (model[pointers.expirationDate]) {
                this.expirationData.time = 'custom';
                this.expirationData.dateString = model[pointers.expirationDate];
            }
            else {
                this.expirationData.time = undefined;
                this.expirationData.dateString = undefined;
            }
        }
    }
    get formData() {
        const { model, pointers, onChange, disabled, expirationDateTitle, expirationPolicyTitle, maxDateSeconds, disableDownloadNever } = this.props;
        const now = new Date();
        const formData = {
            id: 'AsperaExpirationPolicy-ExpirationPolicy',
            model: this.expirationData,
            items: [],
        };
        if (pointers.expirationDate) {
            formData.items.push({
                type: 'select',
                pointer: 'time',
                disabled,
                label: expirationDateTitle || language.get('sendPackage.deleteByTime'),
                required: !!maxDateSeconds,
                onChange: () => {
                    let dateString = undefined;
                    // Handle no value set when dropdown is optional
                    if (!maxDateSeconds && !this.expirationData.time) {
                        this.expirationData.time = '0';
                    }
                    switch (this.expirationData.time) {
                        case '0':
                            dateString = undefined;
                            break;
                        case 'custom':
                            dateString = this.expirationData.dateString;
                            break;
                        default:
                            if (this.expirationData.time) {
                                dateString = new Date(now.getTime() + (Number(this.expirationData.time) * 1000)).toISOString();
                            }
                            else {
                                dateString = '';
                            }
                            break;
                    }
                    if (pointers.expirationDate) {
                        if (pointers.expirationTime)
                            model[pointers.expirationTime] = this.expirationData.time === 'custom' ? undefined : Number(this.expirationData.time);
                        model[pointers.expirationDate] = dateString;
                        this.validCheck();
                        if (onChange)
                            onChange(model[pointers.expirationDate], model);
                    }
                    this.setState({});
                },
                items: [
                    { value: '0', text: language.get('sendPackage.never'), disabled: !!maxDateSeconds },
                    { value: '86400', text: language.get('sendPackage.inTwentyFourHours'), disabled: !this.validTimeCheck(86400) },
                    { value: '604800', text: language.get('sendPackage.inSevenDays'), disabled: !this.validTimeCheck(604800) },
                    { value: '2678400', text: language.get('sendPackage.inThirtyOneDays'), disabled: !this.validTimeCheck(2678400) },
                    { value: 'custom', text: language.get('sendPackage.custom') },
                ],
            });
            if (this.expirationData.time === 'custom' && pointers.expirationDate) {
                formData.items.push({
                    type: 'date',
                    pointer: 'dateString',
                    required: true,
                    disabled,
                    label: language.get('sendPackage.customDate'),
                    dateProps: {
                        minDate: new Date(+now.setHours(0, 0, 0, 0) + 86400000).toISOString(),
                        maxDate: typeof maxDateSeconds === 'number' ? new Date(now.getTime() + (maxDateSeconds * 1000)).toISOString() : undefined,
                    },
                    onChange: () => {
                        model[pointers.expirationDate || ''] = this.expirationData.dateString;
                        this.validCheck();
                        if (onChange)
                            onChange(model[pointers.expirationDate || ''], model);
                    },
                });
            }
        }
        if (pointers.expirationPolicy) {
            formData.items.push({
                type: 'select',
                pointer: 'policy',
                disabled,
                label: expirationPolicyTitle || language.get('sendPackage.deleteByDownload'),
                required: disableDownloadNever,
                onChange: () => {
                    // Handle no value set when dropdown is optional
                    if (!disableDownloadNever && !this.expirationData.policy) {
                        this.expirationData.policy = 'none';
                    }
                    if (pointers.expirationPolicy) {
                        model[pointers.expirationPolicy] = this.expirationData.policy;
                        this.validCheck();
                        if (onChange)
                            onChange(model[pointers.expirationPolicy], model);
                    }
                },
                items: [
                    { value: 'none', text: language.get('sendPackage.never'), disabled: disableDownloadNever },
                    { value: 'delete_after_any_download', text: language.get('sendPackage.deleteAfterAnyDownloadsAll') },
                    { value: 'delete_after_all_download', text: language.get('sendPackage.deleteAfterAllDownloadsAll') },
                ],
            });
        }
        return formData;
    }
    componentDidMount() {
        this.setupModel();
        this.validCheck();
        this.setState({ loading: false });
    }
    componentDidUpdate(previousProps) {
        const { disabled, expirationDateTitle, expirationPolicyTitle, light, maxDateSeconds, disableDownloadNever, pointers } = this.props;
        if (disabled !== previousProps.disabled ||
            expirationDateTitle !== previousProps.expirationDateTitle ||
            expirationPolicyTitle !== previousProps.expirationPolicyTitle ||
            light !== previousProps.light ||
            maxDateSeconds !== previousProps.maxDateSeconds ||
            disableDownloadNever !== previousProps.disableDownloadNever ||
            pointers.expirationDate !== previousProps.pointers.expirationDate ||
            pointers.expirationPolicy !== previousProps.pointers.expirationPolicy ||
            pointers.expirationTime !== previousProps.pointers.expirationTime) {
            this.setupModel();
            this.setState({ loading: true });
            this.subManager.setSubscription(observableTimeout(500).subscribe({
                next: () => {
                    this.setState({ loading: false });
                },
                error: () => {
                    this.setState({ loading: false });
                },
            }));
        }
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    render() {
        const { light, ignoreDirtyRules } = this.props;
        const { loading } = this.state;
        return (React.createElement("div", { className: "aspera-expiration-policy" }, loading ? language.get('pageSystem.loading') : React.createElement(AsperaForm, { formDetails: this.formData, light: light, ignoreDirtyRules: ignoreDirtyRules, className: "expiration-policy" })));
    }
}
